import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { format } from 'date-fns'
import { Card, CardHeader } from '../../ui/card'
import { Badge } from '../../ui/badge'
import Avatar from '../../leevo_ui/Avatar/Avatar'
import { Calendar, Hash, User, Target, Mail, Award } from 'lucide-react'
import { requestCreator } from '../../../utilities/requests'

const { put: updateUser, cancel: cancelUserRequests } = requestCreator()

function ProfileCard({ student }) {
  const [mostRecentPublicId, setMostRecentPublicId] = useState(student.cloudinaryPhotoPublicId)

  useEffect(() => {
    setMostRecentPublicId(student.cloudinaryPhotoPublicId)
    return cancelUserRequests
  }, [student.cloudinaryPhotoPublicId])

  function formatBirthdate(birthdate) {
    if (!birthdate) return null
    try {
      const dateOnly = birthdate.split('T')[0]
      const [year, month, day] = dateOnly.split('-').map(Number)
      const date = new Date(year, month - 1, day)
      return format(date, 'MMM dd, yyyy')
    } catch (e) {
      return null
    }
  }

  function getCoachName() {
    if (student.klassPackages && student.klassPackages.length > 0) {
      const [firstPackage] = student.klassPackages
      if (firstPackage.coaches && firstPackage.coaches.length > 0) {
        return firstPackage.coaches[0].fullName || firstPackage.coaches[0].preferredName
      }
    }
    return null
  }

  const age = student.age || 'N/A'

  const birthdate = formatBirthdate(student.birthdate)
  const coachName = getCoachName()

  const completedSkills = student.studentsSkills?.filter((s) => s.status === 'completed').length || 0
  const totalSkills = student.studentsSkills?.length || 0
  const remainingSkills = totalSkills - completedSkills
  const nextLevelName = student.currentSkillLevel?.nextSkillLevelName || student.currentSkillLevel?.nextSkillLevel?.name
  const hasNextLevel = nextLevelName && remainingSkills > 0

  return (
    <Card className="overflow-hidden h-full">
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
          <Avatar
            fullName={student.fullName}
            isStudent
            cloudinaryPhotoPublicId={mostRecentPublicId}
            alt={`Photo of ${student.fullName}`}
            userId={student.id}
            size={24}
            onPhotoUpload={(newPublicId) => {
              updateUser(`/api/users/${student.id}`, {
                user: { cloudinaryPhotoPublicId: newPublicId }
              }).then(() => {
                setMostRecentPublicId(newPublicId)
              })
            }}
          />

          <div className="flex-1 min-w-0 text-center sm:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 break-words">{student.fullName}</h2>
            {student.preferredName && student.preferredName !== student.fullName && (
              <p className="text-base sm:text-lg text-gray-600 mt-1">&ldquo;{student.preferredName}&rdquo;</p>
            )}

            <div className="flex flex-wrap gap-2 my-4 justify-center sm:justify-start">
              <Badge variant="secondary" className="text-sm px-3 py-1">
                Age {age}
              </Badge>
              <Badge className="text-sm px-3 py-1 bg-purple-600 hover:bg-purple-700">
                {student.currentSkillLevel.name}
              </Badge>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {student.ltsNumber && (
              <div className="flex items-center gap-2">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                  <Hash className="w-4 h-4 text-gray-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">LTS Number</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">{student.ltsNumber}</p>
                </div>
              </div>
            )}

            {birthdate && (
              <div className="flex items-center gap-2">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-gray-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Birthdate</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">{birthdate}</p>
                </div>
              </div>
            )}

            {coachName && (
              <div className="flex items-center gap-2">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Coach</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">{coachName}</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-3">
            {hasNextLevel && (
              <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
                <Target className="w-4 h-4 text-purple-600 flex-shrink-0" />
                <span className="text-sm text-purple-900">
                  <span className="font-semibold">{remainingSkills}</span> more to reach{' '}
                  <span className="font-semibold">{nextLevelName}</span>
                </span>
              </div>
            )}

            {!hasNextLevel && remainingSkills === 0 && totalSkills > 0 && (
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                <Award className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span className="text-sm text-green-900">
                  <span className="font-semibold">Top Level!</span> All skills mastered
                </span>
              </div>
            )}

            {remainingSkills > 0 && (
              <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <Target className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <span className="text-sm text-blue-900">
                  <span className="font-semibold">{completedSkills}</span> of{' '}
                  <span className="font-semibold">{totalSkills}</span> complete
                </span>
              </div>
            )}

            {student.email && (
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <Mail className="w-4 h-4 text-gray-600 flex-shrink-0" />
                <span className="text-sm text-gray-900 truncate">{student.email}</span>
              </div>
            )}
          </div>
        </div>
      </CardHeader>
    </Card>
  )
}

ProfileCard.propTypes = {
  student: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    fullName: PropTypes.string.isRequired,
    preferredName: PropTypes.string,
    email: PropTypes.string,
    cloudinaryPhotoPublicId: PropTypes.string,
    age: PropTypes.number,
    ltsNumber: PropTypes.string,
    birthdate: PropTypes.string,
    currentSkillLevel: PropTypes.shape({
      name: PropTypes.string.isRequired,
      nextSkillLevelName: PropTypes.string,
      nextSkillLevel: PropTypes.shape({
        name: PropTypes.string
      })
    }).isRequired,
    studentsSkills: PropTypes.arrayOf(
      PropTypes.shape({
        status: PropTypes.string
      })
    ),
    klassPackages: PropTypes.arrayOf(
      PropTypes.shape({
        coaches: PropTypes.arrayOf(
          PropTypes.shape({
            fullName: PropTypes.string,
            preferredName: PropTypes.string
          })
        )
      })
    )
  }).isRequired
}

export default ProfileCard
